/**
 * ChatInput - Presentation component for message input area
 * Follows ADR-004: Separation of Concerns - Pure presentation component
 */

import React, { useRef, useState, useEffect, useCallback, forwardRef, memo } from 'react';
import { Send, Loader2 } from 'lucide-react';
import type { AttributionType } from '../types/chat';

/**
 * Props for ChatInput component
 */
interface ChatInputProps {
  /** Current input value */
  value: string;
  /** Callback when input value changes */
  onChange: (value: string) => void;
  /** Callback when form is submitted (Enter key or button click) */
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  /** Placeholder text (also used as aria-label for the input) */
  placeholder: string;
  /** Whether input is disabled */
  disabled: boolean;
  /** Whether to show loading indicator */
  isLoading: boolean;
  /** Attribution type: 'none' | 'enter1nine' */
  attribution?: AttributionType;
  /** When true, the input will auto-focus once and only once (typically after
   * the initial greeting/message has finished rendering). */
  autoFocusWhenReady?: boolean;
}

/**
 * ChatInput - Pure presentation component for chat message input
 * 
 * Uses semantic HTML form structure for proper accessibility and automation support.
 * The form handles both Enter key submission and button click natively.
 * 
 * Automation selectors (per ADR-002 Query Priority):
 * - Input: `getByRole('textbox', { name: /placeholder text/i })`
 * - Button: `getByRole('button', { name: /send message/i })`
 * 
 * @example
 * ```tsx
 * <ChatInput
 *   value={inputMessage}
 *   onChange={setInputMessage}
 *   onSubmit={handleFormSubmit}
 *   placeholder="Type a message..."
 *   disabled={false}
 *   isLoading={false}
 * />
 * ```
 */
/**
 * Attribution configuration for each type
 */
const ATTRIBUTION_CONFIG: Record<Exclude<AttributionType, 'none'>, { name: string; url: string }> = {
  enter1nine: { name: 'Enter1Nine', url: 'https://enter1nine.com/' }
};

export const ChatInput = memo(forwardRef(({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled,
  isLoading,
  attribution = 'enter1nine',
  autoFocusWhenReady = false
}: ChatInputProps, forwardedRef: React.ForwardedRef<HTMLInputElement>) => {
  const attributionInfo = attribution !== 'none' ? ATTRIBUTION_CONFIG[attribution] : null;
  const localRef = useRef<HTMLInputElement>(null);

  // Keep both the local ref and any forwarded ref in sync
  const setRef = useCallback((node: HTMLInputElement | null) => {
    (localRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
    if (typeof forwardedRef === 'function') {
      forwardedRef(node);
    } else if (forwardedRef) {
      (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
    }
  }, [forwardedRef]);
  
  // Track actual DOM input value for button disabled state
  // This ensures automation tools that bypass React events still work
  const [hasContent, setHasContent] = useState(false);
  
  // Update hasContent based on both React state and DOM value
  useEffect(() => {
    const checkContent = (): void => {
      const domValue = localRef.current?.value || '';
      setHasContent(!!value.trim() || !!domValue.trim());
    };
    checkContent();
    
    // Also poll for DOM changes (for automation tools that set value directly)
    const interval = setInterval(checkContent, 100);
    return () => clearInterval(interval);
  }, [value]);

  // Auto-focus the input once the parent says it's ready (typically after the
  // initial greeting has finished rendering). A sticky ref flag ensures this
  // only happens once, even across React StrictMode double-invoke. We
  // intentionally do NOT return a cleanup, otherwise the timer would be
  // cancelled before it has a chance to fire.
  const hasAutoFocused = useRef<boolean>(false);
  useEffect(() => {
    if (!autoFocusWhenReady || disabled || hasAutoFocused.current) return;
    window.setTimeout(() => {
      const el = localRef.current;
      if (!el || hasAutoFocused.current || el.disabled) return;
      el.focus({ preventScroll: true });
      if (document.activeElement === el) {
        hasAutoFocused.current = true;
      }
    }, 50);
  }, [autoFocusWhenReady, disabled]);

  /**
   * Handles input changes from both onChange and onInput events
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | React.FormEvent<HTMLInputElement>): void => {
    const newValue = (e.target as HTMLInputElement).value;
    onChange(newValue);
    setHasContent(!!newValue.trim());
  };

  return (
    <div className="chatbot-input-container bg-card border-t border-border p-2 sm:p-3 m-1 sm:m-2 rounded-md">
      <form 
        onSubmit={onSubmit}
        className="chatbot-input-form flex items-center space-x-2 sm:space-x-3"
      >
        <input
          ref={setRef}
          type="text"
          name="chat-message-input"
          value={value}
          onChange={handleInputChange}
          onInput={handleInputChange}
          placeholder={placeholder}
          aria-label={placeholder}
          disabled={disabled}
          // Disable browser autofill/autocomplete across all browsers
          autoComplete="one-time-code"
          autoCorrect="off"
          autoCapitalize="off"
          data-form-type="other"
          data-lpignore="true"
          // Disable browser-generated tooltips
          title=""
          className="chatbot-input-field flex-1 h-12 bg-background border border-input focus:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 text-foreground placeholder-muted-foreground rounded-md px-4 text-sm transition-all duration-200 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
        />
        <button
          type="submit"
          aria-label="Send message"
          disabled={!hasContent || disabled}
          className="chatbot-send-button w-12 h-12 bg-primary hover:bg-primary/90 disabled:bg-muted disabled:cursor-not-allowed text-primary-foreground rounded-md transition-all duration-200 flex items-center justify-center flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {isLoading ? <Loader2 size={20} className="chatbot-send-button-icon animate-spin" /> : <Send size={20} className="chatbot-send-button-icon" />}
        </button>
      </form>

      {/* Powered by attribution */}
      {attributionInfo && (
        <div className="chatbot-attribution flex items-center justify-center mt-2 sm:mt-3">
          <a
            href={attributionInfo.url}
            target="_blank"
            rel="noopener noreferrer"
            className="chatbot-attribution-link text-xs text-muted-foreground hover:text-foreground transition-colors duration-200 flex items-center space-x-1"
          >
            <span>Powered by</span>
            <span className="font-semibold">{attributionInfo.name}</span>
          </a>
        </div>
      )}
    </div>
  );
}));

ChatInput.displayName = 'ChatInput';

